<template>
  <div class="progress-container">
    <svg :width="size" :height="size" viewBox="0 0 100 100">
      <defs>
        <linearGradient
          id="gradient"
          x1="71.7879"
          y1="6.09445"
          x2="122.37"
          y2="98.1452"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#5639C6">
            <animate
              v-if="animateProgress"
              attributeName="stop-color"
              values="#5639C6; #328CBE; #5639C6"
              dur="4s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="1" stop-color="#328CBE">
            <animate
              v-if="animateProgress"
              attributeName="stop-color"
              values="#328CBE; #5639C6; #328CBE"
              dur="4s"
              repeatCount="indefinite"
            />
          </stop>
        </linearGradient>
        <linearGradient
          id="progress_holder"
          x1="60.0497"
          y1="186.157"
          x2="-18.2377"
          y2="15.9728"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#3E3F49" />
          <stop offset="1" stop-color="#3E3F49" stop-opacity="0" />
        </linearGradient>
      </defs>
      <circle
        class="progress-container__background"
        cx="50"
        cy="50"
        :r="radius"
        fill="none"
        :stroke-width="strokeWidth"
      />
      <circle
        class="progress-container__placeholder"
        cx="50"
        cy="50"
        :r="radius"
        fill="none"
        :stroke="'url(#progress_holder)'"
        :stroke-width="strokeWidth"
        :stroke-dasharray="circumference"
        :stroke-linecap="'round'"
        transform="rotate(-90 50 50)"
      />
      <circle
        class="progress-container__bar"
        cx="50"
        cy="50"
        :r="radius"
        fill="none"
        :stroke="'url(#gradient)'"
        :stroke-width="strokeWidth"
        :stroke-dasharray="circumference"
        :stroke-dashoffset="dashOffset"
        :stroke-linecap="'round'"
      />
      <circle
        class="progress-container__inner-circle"
        cx="50"
        cy="50"
        :r="innerRadius"
        fill="#1E1E1E"
      />
      <defs>
        <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop
            offset="0%"
            style="stop-color:rgba(62, 63, 73, 0.7); stop-opacity:1"
          />
          <stop
            offset="100%"
            style="stop-color:rgba(62, 63, 73, 0); stop-opacity:1"
          />
        </linearGradient>

        <radialGradient
          id="borderGradient1"
          cx="-53.25%"
          cy="10.23%"
          r="94.31%"
        >
          <stop offset="0%" style="stop-color:#FFFFFF; stop-opacity:1" />
          <stop
            offset="100%"
            style="stop-color:rgba(255, 255, 255, 0.06); stop-opacity:1"
          />
        </radialGradient>

        <radialGradient
          id="borderGradient2"
          cx="129.28%"
          cy="66.93%"
          r="86.84%"
        >
          <stop offset="0%" style="stop-color:#DBBEFF; stop-opacity:1" />
          <stop
            offset="100%"
            style="stop-color:rgba(219, 190, 255, 0); stop-opacity:1"
          />
        </radialGradient>

        <radialGradient
          id="borderGradient3"
          cx="51.09%"
          cy="-22.85%"
          r="41.06%"
        >
          <stop offset="0%" style="stop-color:#C6C6C6; stop-opacity:1" />
          <stop
            offset="100%"
            style="stop-color:rgba(255, 255, 255, 0); stop-opacity:1"
          />
        </radialGradient>

        <mask id="circleMask">
          <circle cx="50" cy="50" r="38" fill="white" />
          <circle cx="50" cy="50" r="37" fill="black" />
        </mask>
      </defs>

      <g mask="url(#circleMask)">
        <rect
          x="0"
          y="0"
          width="100"
          height="100"
          fill="url(#borderGradient1)"
        />
        <rect
          x="0"
          y="0"
          width="100"
          height="100"
          fill="url(#borderGradient2)"
        />
        <rect
          x="0"
          y="0"
          width="100"
          height="100"
          fill="url(#borderGradient3)"
        />
      </g>

      <circle cx="50" cy="50" r="37" fill="url(#gradient1)" />
      <foreignObject x="27" y="21" width="45" height="58.93">
        <div class="progress-container__icon-wrapper">
          <SkillCheckIcon class="progress-container__icon-flag" />
          <span class="progress-container__center-text">{{ percentage }}%</span>
        </div>
      </foreignObject>
    </svg>
  </div>
</template>
<script>
import SkillCheckIcon from '@/assets/icons/skill_check_icon.svg'

export default {
  name: 'CpeLabProgress',
  components: {
    SkillCheckIcon,
  },
  props: {
    size: {
      type: Number,
      default: 134,
    },
    strokeWidth: {
      type: Number,
      default: 7,
    },
    maxValue: {
      type: Number,
      default: 10,
    },
    currentValue: {
      type: Number,
      default: 10,
    },
    animateProgress: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    radius() {
      return 49 - this.strokeWidth / 2
    },
    circumference() {
      return 2 * Math.PI * this.radius
    },
    dashOffset() {
      const progress = this.currentValue / this.maxValue
      return this.circumference * (1 - progress)
    },
    innerRadius() {
      return this.radius - this.strokeWidth / 2
    },
    percentage() {
      return Math.round((this.currentValue / this.maxValue) * 100)
    },
  },
}
</script>

<style lang="scss" scoped>
.progress-container {
  background-color: var(--charcoal);
  border-radius: 50%;
  padding: 6px;
  width: 147px;
  height: 147px;

  &__background {
    stroke: var(--charcoal);
  }

  &__placeholder {
    transition: stroke-dashoffset 0.3s ease;
  }

  &__bar {
    transform: rotate(-90deg);
    transform-origin: 50% 50%;
    transition: stroke-dashoffset 0.3s ease;
  }

  &__inner-circle {
    stroke: var(--charcoal);
    stroke-width: 1;
  }

  &__center-circle {
    border: 1px solid;
    background: var(--cpe-progress-center-gradient);
    border-image-source: var(--cpe-progress-border-gradient);
  }

  &__text {
    font-family: Arial, sans-serif;
    font-size: 14px;
    fill: var(--white);
    font-weight: bold;
  }

  &__icon-wrapper {
    display: flex;
    text-align: center;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    position: absolute;
    width: 45px;
    height: 60px;
    z-index: 10;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
  }

  &__center-text {
    color: var(--white);
    font-size: 14px;
    line-height: 24px;
  }

  &__icon-flag {
    height: 26px;
    width: 26px;
  }

  &__radial-border-content {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: var(--charcoal);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--white);
  }

  &__radial-border-container {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 50%;
      padding: 5px;
      background: var(--cpe-progress-radial-gradient);
      -webkit-mask: radial-gradient(circle, transparent 60%, black 62%);
      mask: radial-gradient(circle, transparent 60%, black 62%);
    }
  }

  &__flag-circle {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    border: 1px solid transparent;
    border-width: 1px 0px 0px 0px;
    background: var(--cpe-progress-flag-circle);
  }
}
</style>
